import React from "react";
import { IconButton, Box } from "@chakra-ui/react";
import { FaComments } from "react-icons/fa";
import { useUnreadStore } from "../store/useOrderStore";
import { useChatContext } from "../../../context/chatContext";
import { ChatType } from "../../../types/chatTypes";

interface ChatButtonProps {
  chatType: ChatType;
  participantName: string;
  participantId: string;
  orderNo: string;
  orderHandlerUserId: string;
  isOrderHandler: boolean;
  isDisabled?: boolean;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  variant?: "ghost" | "solid" | "outline";
  customStyles?: {
    iconColor?: string;
    iconColorUnread?: string;
    hoverStyle?: any;
    hoverStyleUnread?: any;
  };
}

const ChatButton: React.FC<ChatButtonProps> = ({
  chatType,
  participantName,
  participantId,
  orderNo,
  orderHandlerUserId,
  isOrderHandler,
  isDisabled = false,
  size = "xs",
  variant = "ghost",
  customStyles,
}) => {
  const {
    setOpenSupplierChatUniqueName,
    setOpenSupplierChatData,
    setOpenCustomerChatUniqueName,
    setOpenCustomerChatData,
    isConnected,
  } = useChatContext();
  const { getUnreadCountByUniqueName } = useUnreadStore();

  const uniqueName = `order:${orderNo}|${chatType}:${participantId}`;
  const unreadCount = getUnreadCountByUniqueName(uniqueName);
  const disabled =
    isDisabled || !isOrderHandler || !isConnected || !participantId;

  const hasUnread = unreadCount > 0;

  const getIconColor = () => {
    if (customStyles?.iconColor && !hasUnread) {
      return customStyles.iconColor;
    }
    if (customStyles?.iconColorUnread && hasUnread) {
      return customStyles.iconColorUnread;
    }
    if (hasUnread) {
      return chatType === ChatType.CUSTOMER ? "green.400" : "blue.400";
    } else {
      return chatType === ChatType.CUSTOMER ? "green.600" : "blue.600";
    }
  };

  const getHoverStyle = () => {
    if (customStyles?.hoverStyle && !hasUnread) {
      return customStyles.hoverStyle;
    }
    if (customStyles?.hoverStyleUnread && hasUnread) {
      return customStyles.hoverStyleUnread;
    }
    const iconColor = getIconColor();
    if (hasUnread) {
      return chatType === ChatType.CUSTOMER
        ? { bg: iconColor, color: "white" }
        : { bg: iconColor, color: "white" };
    } else {
      return chatType === ChatType.CUSTOMER
        ? { bg: "green.50" }
        : { bg: "blue.50" };
    }
  };

  if (disabled) {
    const isDisabledByConnection = !isConnected;

    let tooltipText = `Chat with ${participantName}`;
    if (isDisabledByConnection) {
      tooltipText = "Connecting to chat...";
    } else if (!participantId) {
      tooltipText = `${
        chatType === ChatType.SUPPLIER ? "Supplier" : "Customer"
      } not available for chat`;
    }

    return (
      <IconButton
        aria-label={tooltipText}
        icon={<FaComments size={16} />}
        size={size}
        variant={variant}
        isDisabled={true}
        colorScheme="gray"
        color="gray.400"
        _hover={getHoverStyle()}
        title={tooltipText}
      />
    );
  }

  return (
    <Box position="relative">
      <IconButton
        aria-label={`Chat with ${participantName}`}
        icon={<FaComments size={16} />}
        size={size}
        variant={variant}
        onClick={() => {
          const chatData = {
            chatType,
            orderNumber: orderNo,
            participantName,
            orderHandlerUserId,
            participantId,
            uniqueName,
          };

          if (chatType === ChatType.SUPPLIER) {
            setOpenSupplierChatData(chatData as any);
            setOpenSupplierChatUniqueName(uniqueName);
          } else {
            setOpenCustomerChatData(chatData as any);
            setOpenCustomerChatUniqueName(uniqueName);
          }
        }}
        color={getIconColor()}
        _hover={getHoverStyle()}
      />
    </Box>
  );
};

export default ChatButton;
